import { FC, ReactNode, useEffect, useState } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import RichText from '@eneco/components/RichText/RichText';
import { postEnecoBeXapiSiteApiV1PaymentsCallback } from '@eneco/dc-be/client';
import { useRouter } from '@eneco/next';
import { ButtonLink, NotificationBox } from '@eneco/sparky';
import { useContent } from '@sitecore/common';
import { PaymentConfirmationRendering } from '@sitecore/types/PaymentConfirmation';

import CenteredCardLayout from '../../../../components/CenteredCardLayout';
import DataErrorNotification from '../../../../components/DataErrorNotification';
import { RoundedSkeleton } from '../../../../components/Loading/RoundedSkeleton';

const COMPLETED_RESPONSE_CODES = ['00', '100', '110', '200', '300'];
const ERROR_RESPONSE_CODES = ['03', '05', '12', '17', '90', '99', '101', '201', '301'];

const PaymentConfirmation: FC = () => {
  const { fields } = useContent<PaymentConfirmationRendering>();
  const {
    selectedAccount: { crmAccountNumber },
  } = useSelfServiceAccount();
  const { data: session } = useSession();
  const { query } = useRouter();
  const responseCode = query.responseCode as string;
  const pomDocumentId = query.documentId as string;
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(ERROR_RESPONSE_CODES.includes(responseCode ?? ''));

  const isCompleted = COMPLETED_RESPONSE_CODES.includes(responseCode ?? '') && !isLoading;

  useEffect(() => {
    const sendCallback = async () => {
      if (!isError && isLoading && session) {
        try {
          const { response } = await postEnecoBeXapiSiteApiV1PaymentsCallback({
            body: {
              responseCode,
              pomDocumentId,
            },
          });
          if (!response.ok) throw new Error('Failed to send callback');
          setIsError(false);
        } catch (error) {
          void error;
          setIsError(true);
        } finally {
          setIsLoading(false);
        }
      }
    };

    void sendCallback();
  }, [isError, isLoading, responseCode, pomDocumentId, crmAccountNumber, session]);

  let notification: ReactNode | null = null;

  if (isLoading) {
    notification = <RoundedSkeleton height={78} />;
  }

  if (!responseCode || !pomDocumentId) {
    notification = <DataErrorNotification />;
  } else if (isCompleted) {
    notification = (
      <NotificationBox
        isAlert={false}
        variant={fields.data.successNotification.value.variant}
        title={fields.data.successNotification.value.title}
        text={<RichText html={fields.data.successNotification.value.content} />}
      />
    );
  } else if (isError) {
    notification = (
      <NotificationBox
        isAlert={false}
        variant={fields.data.errorNotification.value.variant}
        title={fields.data.errorNotification.value.title}
        text={<RichText html={fields.data.errorNotification.value.content} />}
      />
    );
  }

  return (
    <CenteredCardLayout>
      {notification}
      <ButtonLink href={fields.data.primaryButtonLink.value.href}>
        {fields.data.primaryButtonLink.value.text}
      </ButtonLink>
    </CenteredCardLayout>
  );
};

export default PaymentConfirmation;
